import type { Message, Model, Part, Session, SnapshotFileDiff, UserMessage } from "@opencode-ai/sdk/v2"
import { Icon } from "@opencode-ai/ui/icon"
import { Markdown } from "@opencode-ai/ui/markdown"
import { DataProvider, DialogProvider, I18nProvider, useDialog, type UiI18nParams } from "@opencode-ai/ui/context"
import { Dialog } from "@opencode-ai/ui/dialog"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { FileComponentProvider } from "@opencode-ai/ui/context/file"
import { MarkedProvider } from "@opencode-ai/ui/context/marked"
import { WorkerPoolProvider } from "@opencode-ai/ui/context/worker-pool"
import { File } from "@opencode-ai/ui/file"
import { dict as uiEn } from "@opencode-ai/ui/i18n/en"
import { dict as uiRu } from "@opencode-ai/ui/i18n/ru"
import { getWorkerPools } from "@opencode-ai/ui/pierre/worker"
import { SessionReview, type SessionReviewDiffStyle } from "@opencode-ai/ui/session-review"
import { SessionTurn } from "@opencode-ai/ui/session-turn"
import { createMemo, createResource, createSignal, For, Match, onCleanup, Show, Switch } from "solid-js"

// --- Viewer-own strings (everything inside messages comes from the ui dicts) --

const STRINGS = {
  ru: {
    "viewer.tagline": "публичная копия чата",
    "viewer.loading": "Загружаем чат…",
    "viewer.notFound.title": "Здесь ничего нет",
    "viewer.notFound.body": "Публикация не найдена — возможно, автор отменил её или ссылка неполная.",
    "viewer.error.title": "Не получилось загрузить чат",
    "viewer.error.body": "Попробуйте обновить страницу чуть позже.",
    "viewer.empty": "В этой публикации пока нет сообщений.",
    "viewer.attachmentsNotice": "Вложения не публикуются — файлы остаются на компьютере автора.",
    "viewer.diffs": "Изменённые файлы",
    "viewer.files": "Файлы из чата",
    "viewer.files.download": "Скачать",
    "viewer.files.notPublished.title": "Файл не публиковался",
    "viewer.files.notPublished.body":
      "Этот файл не входит в публикацию — он остался на компьютере автора. Публикуются файлы до 1 МБ поддерживаемых типов.",
    "viewer.files.binaryHint": "Просмотр недоступен — скачайте файл.",
    "viewer.artifact.subtitle": "Документ, опубликованный из MultiTool",
    "viewer.footer": "Опубликовано из MultiTool",
    "viewer.messages.one": "сообщение",
    "viewer.messages.few": "сообщения",
    "viewer.messages.many": "сообщений",
  },
  en: {
    "viewer.tagline": "public copy of a chat",
    "viewer.loading": "Loading the chat…",
    "viewer.notFound.title": "Nothing here",
    "viewer.notFound.body": "This share does not exist — the author may have unpublished it, or the link is incomplete.",
    "viewer.error.title": "Could not load the chat",
    "viewer.error.body": "Try refreshing the page a bit later.",
    "viewer.empty": "This share has no messages yet.",
    "viewer.attachmentsNotice": "Attachments are not published — files stay on the author's computer.",
    "viewer.diffs": "Changed files",
    "viewer.files": "Files from the chat",
    "viewer.files.download": "Download",
    "viewer.files.notPublished.title": "File was not published",
    "viewer.files.notPublished.body":
      "This file is not part of the publication — it stayed on the author's computer. Files up to 1 MB of supported types are published.",
    "viewer.files.binaryHint": "No preview available — download the file.",
    "viewer.artifact.subtitle": "A document published from MultiTool",
    "viewer.footer": "Published from MultiTool",
    "viewer.messages.one": "message",
    "viewer.messages.few": "messages",
    "viewer.messages.many": "messages",
  },
} as const

type ViewerKey = keyof (typeof STRINGS)["ru"]

function detectLocale(): "ru" | "en" {
  if (typeof navigator !== "object") return "ru"
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const language of languages) {
    if (!language) continue
    if (language.toLowerCase().startsWith("ru")) return "ru"
    if (language.toLowerCase().startsWith("en")) return "en"
  }
  return "ru"
}

function resolveTemplate(text: string, params?: UiI18nParams) {
  if (!params) return text
  return text.replace(/{{\s*([^}]+?)\s*}}/g, (_, rawKey) => {
    const value = params[String(rawKey)]
    return value === undefined ? "" : String(value)
  })
}

function pluralMessages(locale: "ru" | "en", n: number): ViewerKey {
  if (locale === "en") return n === 1 ? "viewer.messages.one" : "viewer.messages.many"
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return "viewer.messages.one"
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "viewer.messages.few"
  return "viewer.messages.many"
}

// --- Share data loading -------------------------------------------------------

type ShareItem =
  | { type: "session"; data: Session }
  | { type: "message"; data: Message }
  | { type: "part"; data: Part }
  | { type: "session_diff"; data: SnapshotFileDiff[] }
  | { type: "model"; data: Model[] }
  | { type: "artifact"; data: ShareArtifact }
  | { type: "file"; data: ShareArtifact }

export interface ShareArtifact {
  filename: string
  mime: string
  /** Text files carry text (rendered); small binaries carry base64 (download). */
  text?: string
  base64?: string
}

interface ShareData {
  sessionID: string
  info: Session
  data: {
    session: Session[]
    session_status: Record<string, { type: "idle" }>
    session_diff: Record<string, SnapshotFileDiff[]>
    message: Record<string, Message[]>
    part: Record<string, Part[]>
  }
  models: Model[]
  diffs: SnapshotFileDiff[]
  // Chat result-files published with the snapshot (small documents) — links in
  // messages point at them via #share-file=<name> fragments. A Map, not a
  // plain object: the filename key comes off the wire, and "__proto__" must
  // stay an ordinary key instead of polluting a record's prototype.
  files: Map<string, ShareArtifact>
}

type LoadResult =
  | { state: "ok"; share: ShareData }
  | { state: "artifact"; artifact: ShareArtifact }
  | { state: "missing" }
  | { state: "error" }

// The share id is the only path segment: share.multitool.works/<id>. The
// upstream /share/<id> form is tolerated so pasted opncd-style paths work.
export function parseShareIdFromPath(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean)
  const id = segments[segments.length - 1]
  if (!id || (segments.length === 2 && segments[0] !== "share") || segments.length > 2) return null
  return /^[A-Za-z0-9_-]{4,64}$/.test(id) ? id : null
}

export function assembleShareData(items: ShareItem[]): ShareData | null {
  const sessions: Session[] = []
  const message: Record<string, Message[]> = {}
  const part: Record<string, Part[]> = {}
  const files = new Map<string, ShareArtifact>()
  let diffs: SnapshotFileDiff[] = []
  let models: Model[] = []
  for (const item of items) {
    switch (item.type) {
      case "file":
        if (
          item.data &&
          typeof item.data.filename === "string" &&
          (typeof item.data.text === "string" || typeof item.data.base64 === "string")
        ) {
          files.set(item.data.filename, item.data)
        }
        break
      case "session":
        sessions.push(item.data)
        break
      case "message": {
        const list = (message[item.data.sessionID] ??= [])
        list.push(item.data)
        break
      }
      case "part": {
        const list = (part[item.data.messageID] ??= [])
        list.push(item.data)
        break
      }
      case "session_diff":
        if (Array.isArray(item.data)) diffs = item.data
        break
      case "model":
        if (Array.isArray(item.data)) models = item.data
        break
    }
  }
  // The snapshot carries exactly one session; ids are ULIDs, so sorting by id
  // is chronological order.
  const info = sessions[0]
  if (!info) return null
  for (const list of Object.values(message)) list.sort((a, b) => a.id.localeCompare(b.id))
  for (const list of Object.values(part)) list.sort((a, b) => a.id.localeCompare(b.id))
  return {
    sessionID: info.id,
    info,
    data: {
      session: sessions.sort((a, b) => a.id.localeCompare(b.id)),
      session_status: { [info.id]: { type: "idle" } },
      session_diff: { [info.id]: diffs },
      message,
      part,
    },
    models,
    diffs,
    files,
  }
}

async function loadShare(id: string): Promise<LoadResult> {
  try {
    const response = await fetch(`/api/share/${id}/data`)
    if (response.status === 404) return { state: "missing" }
    if (!response.ok) return { state: "error" }
    const items = (await response.json()) as ShareItem[]
    const artifactItem = items.find((item): item is Extract<ShareItem, { type: "artifact" }> => item.type === "artifact")
    if (artifactItem && (typeof artifactItem.data?.text === "string" || typeof artifactItem.data?.base64 === "string")) {
      return { state: "artifact", artifact: artifactItem.data }
    }
    const share = assembleShareData(items)
    if (!share) return { state: "missing" }
    return { state: "ok", share }
  } catch {
    return { state: "error" }
  }
}

// Rendering mode for a published file: markdown renders rich, other text is
// shown verbatim (never executed — an .html artifact is displayed as source,
// not served as a live page), images preview inline, other binaries are
// download-only.
export function artifactRenderMode(artifact: ShareArtifact): "markdown" | "text" | "image" | "pdf" | "download" {
  if (typeof artifact.base64 === "string") {
    if (artifact.mime.startsWith("image/")) return "image"
    if (artifact.mime === "application/pdf") return "pdf"
    // Office documents and anything else binary: no in-browser preview.
    return "download"
  }
  if (artifact.mime === "text/markdown") return "markdown"
  const name = artifact.filename.toLowerCase()
  if (name.endsWith(".md") || name.endsWith(".markdown")) return "markdown"
  return "text"
}

// A published file has a real preview (renders inline) vs. download-only.
export function shareFileHasPreview(file: ShareArtifact): boolean {
  return artifactRenderMode(file) !== "download"
}

function shareFileDataUrl(file: ShareArtifact): string {
  return `data:${file.mime};base64,${file.base64 ?? ""}`
}

// PDFs render inline via a blob: URL in an iframe (browsers have a native PDF
// viewer). A blob URL — not the multi-MB data: URL — keeps the iframe src small
// and is revoked on cleanup.
function PdfPreview(props: { file: ShareArtifact }) {
  const bytes = Uint8Array.from(atob(props.file.base64 ?? ""), (c) => c.charCodeAt(0))
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }))
  onCleanup(() => URL.revokeObjectURL(url))
  return <iframe src={url} title={props.file.filename} class="w-full h-[70vh] border-0 rounded-md" />
}

// --- App ------------------------------------------------------------------

function ArtifactContent(props: { artifact: ShareArtifact; locale: "ru" | "en"; t: (key: ViewerKey) => string }) {
  const mode = artifactRenderMode(props.artifact)
  return (
    <main class="w-full max-w-200 mx-auto px-4 sm:px-6 pt-10 pb-16 flex-1 min-w-0">
      <div class="flex flex-col gap-2 pb-6 border-b border-border-weak-base">
        <h1 class="text-18-medium text-text-strong break-all">{props.artifact.filename}</h1>
        <div class="text-12-regular text-text-weaker">{props.t("viewer.artifact.subtitle")}</div>
      </div>
      <div class="mt-8">
        <Switch
          fallback={
            <pre class="text-12-mono text-text-base whitespace-pre-wrap break-words overflow-x-auto">
              {props.artifact.text}
            </pre>
          }
        >
          <Match when={mode === "markdown"}>
            <Markdown text={props.artifact.text ?? ""} linkArtifacts={false} />
          </Match>
          <Match when={mode === "image"}>
            <ImagePreview file={props.artifact} />
          </Match>
          <Match when={mode === "pdf"}>
            <PdfPreview file={props.artifact} />
          </Match>
          <Match when={mode === "download"}>
            <div class="flex items-center gap-3 py-4">
              <span class="text-12-regular text-text-weak">{props.t("viewer.files.binaryHint")}</span>
              <DownloadButton file={props.artifact} label={props.t("viewer.files.download")} />
            </div>
          </Match>
        </Switch>
      </div>
    </main>
  )
}

function CenteredNotice(props: { title: string; body: string }) {
  return (
    <div class="flex-1 flex flex-col items-center justify-center gap-2 px-6 py-24 text-center">
      <div class="text-16-medium text-text-strong">{props.title}</div>
      <div class="text-13-regular text-text-weaker max-w-100">{props.body}</div>
    </div>
  )
}

function DownloadButton(props: { file: ShareArtifact; label: string }) {
  return (
    <button
      type="button"
      class="inline-flex items-center gap-1.5 text-12-medium text-text-strong border border-border-weak-base rounded-md px-3 py-1.5 cursor-pointer bg-surface-base hover:bg-surface-base-active"
      onClick={() => downloadShareFile(props.file)}
      aria-label={props.label}
    >
      <Icon name="download" size="small" />
      {props.label}
    </button>
  )
}

// Centered, bounded image preview: a small icon-sized image reads clearly on a
// neutral card instead of being a lonely speck in a big dialog, and a huge one
// is contained (object-contain, capped height) instead of blowing out the
// panel. A subtle checkerboard makes transparency legible.
function ImagePreview(props: { file: ShareArtifact }) {
  return (
    <div
      class="flex items-center justify-center rounded-lg border border-border-weak-base p-3 min-h-40 max-h-[70vh] overflow-auto"
      style={{
        "background-color": "var(--surface-strong, #f4f4f5)",
        "background-image":
          "linear-gradient(45deg,rgba(0,0,0,.05) 25%,transparent 25%,transparent 75%,rgba(0,0,0,.05) 75%),linear-gradient(45deg,rgba(0,0,0,.05) 25%,transparent 25%,transparent 75%,rgba(0,0,0,.05) 75%)",
        "background-size": "16px 16px",
        "background-position": "0 0,8px 8px",
      }}
    >
      <img
        src={shareFileDataUrl(props.file)}
        alt={props.file.filename}
        class="max-w-full max-h-[calc(70vh-1.5rem)] object-contain"
        style={{ "image-rendering": "auto" }}
      />
    </div>
  )
}

function downloadShareFile(file: ShareArtifact) {
  const blob =
    typeof file.base64 === "string"
      ? new Blob([Uint8Array.from(atob(file.base64), (c) => c.charCodeAt(0))], { type: file.mime })
      : new Blob([file.text ?? ""], { type: `${file.mime};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = file.filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function ShareFileDialogContent(props: { file: ShareArtifact; t: (key: ViewerKey) => string }) {
  const mode = artifactRenderMode(props.file)
  return (
    <Dialog title={props.file.filename} class="w-full max-w-[min(92vw,760px)] mx-auto">
      <div class="flex flex-col gap-3 p-6 pt-0 max-h-[70vh]">
        <div class="flex items-center justify-end shrink-0">
          <DownloadButton file={props.file} label={props.t("viewer.files.download")} />
        </div>
        <div class="overflow-y-auto min-h-0">
          <Switch
            fallback={<pre class="text-12-mono text-text-base whitespace-pre-wrap break-words">{props.file.text}</pre>}
          >
            <Match when={mode === "markdown"}>
              <Markdown text={props.file.text ?? ""} linkArtifacts={false} />
            </Match>
            <Match when={mode === "image"}>
              <ImagePreview file={props.file} />
            </Match>
            <Match when={mode === "pdf"}>
              <PdfPreview file={props.file} />
            </Match>
          </Switch>
        </div>
      </div>
    </Dialog>
  )
}

function NoticeDialogContent(props: { title: string; body: string }) {
  return (
    <Dialog title={props.title} class="w-full max-w-[420px] mx-auto">
      <div class="p-6 pt-0 text-12-regular text-text-weak">{props.body}</div>
    </Dialog>
  )
}

function ShareContent(props: { share: ShareData; locale: "ru" | "en"; t: (key: ViewerKey) => string }) {
  const info = () => props.share.info
  const dialog = useDialog()
  const [diffStyle, setDiffStyle] = createSignal<SessionReviewDiffStyle>("unified")

  // Preview when we can actually render it (text/markdown/image/pdf); otherwise
  // (office docs and other binaries) download straight away — no dead dialog.
  const openFile = (file: ShareArtifact) => {
    if (shareFileHasPreview(file)) dialog.show(() => <ShareFileDialogContent file={file} t={props.t} />)
    else downloadShareFile(file)
  }

  // Message text links point at published files via #share-file=<name>
  // fragments; render-time artifact chips (inline-code basenames) come out as
  // gigatool-artifact://<name> anchors. Both resolve against the published
  // files map; anything else internal gets an honest "not published" notice.
  const onContentClick = (event: MouseEvent) => {
    const anchor = (event.target as HTMLElement | null)?.closest?.("a")
    if (!anchor) return
    const href = anchor.getAttribute("href") ?? ""
    let name: string | null = null
    if (href.startsWith("#share-file=")) name = href.slice("#share-file=".length)
    else if (href.startsWith("gigatool-artifact://")) name = href.slice("gigatool-artifact://".length)
    else if (href.startsWith("multitool-artifact://")) name = href.slice("multitool-artifact://".length)
    else if (anchor.dataset.artifactFilename) name = encodeURIComponent(anchor.dataset.artifactFilename)
    if (name === null) return
    event.preventDefault()
    event.stopPropagation()
    let decoded = name
    try {
      decoded = decodeURIComponent(name)
    } catch {}
    const file = props.share.files.get(decoded)
    if (file) openFile(file)
    else
      dialog.show(() => (
        <NoticeDialogContent
          title={props.t("viewer.files.notPublished.title")}
          body={props.t("viewer.files.notPublished.body")}
        />
      ))
  }

  const userMessages = createMemo(() =>
    (props.share.data.message[props.share.sessionID] ?? [])
      .filter((m): m is UserMessage => m.role === "user")
      .sort((a, b) => a.time.created - b.time.created),
  )

  const modelNames = createMemo(() => {
    const ids = new Set<string>()
    for (const m of userMessages()) {
      const modelID = m.model?.modelID
      if (!modelID) continue
      ids.add(props.share.models.find((model) => model.id === modelID)?.name ?? modelID)
    }
    return Array.from(ids)
  })

  const hasStrippedAttachments = createMemo(() =>
    Object.values(props.share.data.part).some((parts) =>
      parts.some((p) => p.type === "file" && typeof p.url === "string" && p.url.startsWith("stripped:")),
    ),
  )

  const dateLine = createMemo(() => {
    const created = info().time?.created
    if (typeof created !== "number") return ""
    return new Intl.DateTimeFormat(props.locale === "ru" ? "ru-RU" : "en-US", {
      dateStyle: "long",
      timeStyle: "short",
    }).format(created)
  })

  const files = createMemo(() => Array.from(props.share.files.values()))

  return (
    <main class="w-full max-w-200 mx-auto px-4 sm:px-6 pt-10 pb-16 flex-1 min-w-0" onClick={onContentClick}>
      <div class="flex flex-col gap-3">
        <h1 class="text-18-medium text-text-strong text-balance">{info().title}</h1>
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-12-regular text-text-weaker">
          <Show when={modelNames().length > 0}>
            <span>{modelNames().join(" · ")}</span>
          </Show>
          <Show when={dateLine()}>
            <span>{dateLine()}</span>
          </Show>
          <span>
            {userMessages().length} {props.t(pluralMessages(props.locale, userMessages().length))}
          </span>
        </div>
        <Show when={hasStrippedAttachments()}>
          <div class="text-12-regular text-text-weaker">{props.t("viewer.attachmentsNotice")}</div>
        </Show>
      </div>

      <Show when={userMessages().length > 0} fallback={<div class="pt-16 text-13-regular text-text-weaker">{props.t("viewer.empty")}</div>}>
        <div class="flex flex-col gap-12 items-start justify-start mt-10">
          <For each={userMessages()}>
            {(message) => (
              <SessionTurn
                sessionID={props.share.sessionID}
                messageID={message.id}
                classes={{
                  root: "min-w-0 w-full relative",
                  content: "flex flex-col justify-between !overflow-visible",
                  container: "w-full",
                }}
              />
            )}
          </For>
        </div>
      </Show>

      <Show when={files().length > 0}>
        <div class="mt-16 border-t border-border-weak-base pt-8">
          <h2 class="text-14-medium text-text-strong pb-4">{props.t("viewer.files")}</h2>
          <div class="flex flex-wrap gap-2">
            <For each={files()}>
              {(file) => (
                <button
                  type="button"
                  class="flex items-center gap-2 min-w-0 max-w-[min(100%,280px)] border border-border-weak-base rounded-lg px-3 py-2 cursor-pointer bg-surface-base hover:bg-surface-base-active"
                  onClick={() => openFile(file)}
                >
                  <FileIcon node={{ path: file.filename, type: "file" }} />
                  <span class="text-12-medium text-text-strong truncate">{file.filename}</span>
                </button>
              )}
            </For>
          </div>
        </div>
      </Show>

      <Show when={props.share.diffs.length > 0}>
        <div class="mt-16 border-t border-border-weak-base pt-8">
          <h2 class="text-14-medium text-text-strong pb-4">{props.t("viewer.diffs")}</h2>
          <SessionReview diffs={props.share.diffs} diffStyle={diffStyle()} onDiffStyleChange={setDiffStyle} />
        </div>
      </Show>
    </main>
  )
}

export function App() {
  const locale = detectLocale()
  const ru = uiRu as Partial<Record<string, string>>
  const uiT = (key: keyof typeof uiEn, params?: UiI18nParams) => {
    const value = locale === "ru" ? (ru[key] ?? uiEn[key]) : uiEn[key]
    return resolveTemplate(value ?? String(key), params)
  }
  const t = (key: ViewerKey) => STRINGS[locale][key] ?? STRINGS.ru[key]

  if (typeof document === "object") document.documentElement.lang = locale

  const shareId = parseShareIdFromPath(typeof location === "object" ? location.pathname : "")
  const [result] = createResource(
    () => shareId,
    (id) => loadShare(id),
  )

  const share = createMemo(() => {
    const value = result()
    return value?.state === "ok" ? value.share : undefined
  })

  const artifact = createMemo(() => {
    const value = result()
    return value?.state === "artifact" ? value.artifact : undefined
  })

  const pageTitle = createMemo(() => {
    const title = share()?.info.title ?? artifact()?.filename
    return title ? `${title} — MultiTool` : "MultiTool"
  })
  createMemo(() => {
    if (typeof document === "object") document.title = pageTitle()
  })

  return (
    <WorkerPoolProvider pools={getWorkerPools()}>
      <DialogProvider>
        <MarkedProvider>
          <I18nProvider value={{ locale: () => locale, t: uiT }}>
            <FileComponentProvider component={File}>
              <div class="min-h-dvh w-full bg-background-stronger text-text-base flex flex-col select-text">
                <header class="h-12 px-4 sm:px-6 flex items-center justify-between shrink-0 bg-background-base border-b border-border-weak-base">
                  <a href="https://multitool.works" class="text-14-medium text-text-strong no-underline">
                    MultiTool
                  </a>
                  <span class="text-12-regular text-text-weaker">{t("viewer.tagline")}</span>
                </header>
                <Switch>
                  <Match when={!shareId}>
                    <CenteredNotice title={t("viewer.notFound.title")} body={t("viewer.notFound.body")} />
                  </Match>
                  <Match when={result.loading}>
                    <div class="flex-1 flex items-center justify-center py-24 text-13-regular text-text-weaker">
                      {t("viewer.loading")}
                    </div>
                  </Match>
                  <Match when={result()?.state === "missing"}>
                    <CenteredNotice title={t("viewer.notFound.title")} body={t("viewer.notFound.body")} />
                  </Match>
                  <Match when={result()?.state === "error"}>
                    <CenteredNotice title={t("viewer.error.title")} body={t("viewer.error.body")} />
                  </Match>
                  <Match when={artifact()}>
                    {(doc) => <ArtifactContent artifact={doc()} locale={locale} t={t} />}
                  </Match>
                  <Match when={share()}>
                    {(loaded) => (
                      <DataProvider data={loaded().data} directory={loaded().info.directory}>
                        <ShareContent share={loaded()} locale={locale} t={t} />
                      </DataProvider>
                    )}
                  </Match>
                </Switch>
                <footer class="py-8 flex items-center justify-center shrink-0">
                  <a href="https://multitool.works" class="text-12-regular text-text-weaker no-underline">
                    {t("viewer.footer")} · multitool.works
                  </a>
                </footer>
              </div>
            </FileComponentProvider>
          </I18nProvider>
        </MarkedProvider>
      </DialogProvider>
    </WorkerPoolProvider>
  )
}
